import { jsPDF } from 'jspdf';
import notoSansBold from './assets/fonts/NotoSans-Bold.ttf?inline';
import notoSansRegular from './assets/fonts/NotoSans-Regular.ttf?inline';
import { BRAND } from './brand';
import type { Foto, Protokoll } from './domain/types';
import { UEBERSETZUNGEN, type SpracheCode } from './uebersetzungen';

const SEITENBREITE = 210;
const SEITENHOEHE = 297;
const RAND = 15;
const INHALTSBREITE = SEITENBREITE - RAND * 2;
const FUSS_Y = 289;
const PDF_SCHRIFT = 'NotoSans';

interface PdfErgebnis {
	blob: Blob;
	dateiname: string;
}

function dateinameFuer(protokoll: Protokoll): string {
	const adresse = [protokoll.objekt.strasse, protokoll.objekt.ort]
		.filter(Boolean)
		.join('-')
		.toLowerCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.replace(/ß/g, 'ss')
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-|-$/g, '');
	const datum = new Date(protokoll.abgeschlossenAm ?? protokoll.erstelltAm).toISOString().slice(0, 10);
	return `${BRAND.shortName.toLowerCase()}-${protokoll.art}-${adresse || 'ohne-adresse'}-${datum}.pdf`;
}

function fotoFormat(foto: Foto): 'JPEG' | 'PNG' | 'WEBP' {
	if (foto.datenUrl.startsWith('data:image/png')) return 'PNG';
	if (foto.datenUrl.startsWith('data:image/webp')) return 'WEBP';
	return 'JPEG';
}

function sichereZeichen(text: string): string {
	return text
		.replace(/[–—]/g, '-')
		.replace(/[„“”]/g, '"')
		.replace(/[’]/g, "'");
}

const PDF_ZEICHEN =
	/^[\u0009\u000a\u000d\u0020-\u024f\u0300-\u04ff\u2000-\u206f\u20a0-\u20cf\u2100-\u214f]$/u;

function enthaeltNichtUnterstuetzteZeichen(wert: unknown): boolean {
	if (typeof wert === 'string') {
		if (wert.startsWith('data:image/')) return false;
		return Array.from(wert).some((zeichen) => !PDF_ZEICHEN.test(zeichen));
	}
	if (Array.isArray(wert)) return wert.some(enthaeltNichtUnterstuetzteZeichen);
	if (wert && typeof wert === 'object') {
		return Object.values(wert as Record<string, unknown>).some(enthaeltNichtUnterstuetzteZeichen);
	}
	return false;
}

function schriftBase64(datenUrl: string): string {
	const trennzeichen = datenUrl.indexOf(',');
	if (trennzeichen < 0) throw new Error('PDF-Schrift konnte nicht geladen werden.');
	return datenUrl.slice(trennzeichen + 1);
}

function registrierePdfSchrift(doc: jsPDF): void {
	doc.addFileToVFS('NotoSans-Regular.ttf', schriftBase64(notoSansRegular));
	doc.addFont('NotoSans-Regular.ttf', PDF_SCHRIFT, 'normal');
	doc.addFileToVFS('NotoSans-Bold.ttf', schriftBase64(notoSansBold));
	doc.addFont('NotoSans-Bold.ttf', PDF_SCHRIFT, 'bold');
}

/**
 * Erzeugt eine echte PDF-Datei vollständig im Browser. Bilder und
 * Unterschriften werden direkt aus dem Offline-Protokoll eingebettet; kein
 * Upload und kein Server-Aufruf.
 */
export async function erstelleProtokollPdf(protokoll: Protokoll, sprache: SpracheCode): Promise<PdfErgebnis> {
	if (sprache === 'ar' || enthaeltNichtUnterstuetzteZeichen(protokoll)) {
		throw new Error(
			'Arabisch oder einzelne seltene Zeichen benötigen noch eine weitere PDF-Schrift. Bitte dafür vorerst „Drucken“ verwenden.'
		);
	}

	const texte = UEBERSETZUNGEN[sprache];
	const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
	registrierePdfSchrift(doc);
	let y = RAND;
	let seite = 1;

	const setzeTextfarbe = (r = 24, g = 32, b = 28) => doc.setTextColor(r, g, b);
	const fuss = () => {
		doc.setDrawColor(218, 222, 219);
		doc.line(RAND, FUSS_Y - 5, SEITENBREITE - RAND, FUSS_Y - 5);
		doc.setFont(PDF_SCHRIFT, 'normal');
		doc.setFontSize(7.5);
		doc.setTextColor(105, 112, 108);
		doc.text(`${texte.erstelltMit} ${BRAND.name}`, RAND, FUSS_Y);
		doc.text(`${seite}`, SEITENBREITE - RAND, FUSS_Y, { align: 'right' });
	};
	const neueSeite = () => {
		fuss();
		doc.addPage();
		seite += 1;
		y = RAND;
	};
	const braucht = (hoehe: number) => {
		if (y + hoehe > FUSS_Y - 10) neueSeite();
	};
	const zeilen = (text: string, breite = INHALTSBREITE): string[] =>
		doc.splitTextToSize(sichereZeichen(text), breite) as string[];
	const abschnitt = (titel: string) => {
		braucht(18);
		doc.setFillColor(233, 241, 236);
		doc.roundedRect(RAND, y, INHALTSBREITE, 10, 1.5, 1.5, 'F');
		doc.setFont(PDF_SCHRIFT, 'bold');
		doc.setFontSize(11);
		doc.setTextColor(15, 91, 67);
		doc.text(sichereZeichen(titel), RAND + 4, y + 6.7);
		y += 16;
	};
	const bild = (foto: Foto, x: number, bildY: number, boxBreite: number, boxHoehe: number) => {
		try {
			const eigenschaften = doc.getImageProperties(foto.datenUrl);
			const faktor = Math.min(boxBreite / eigenschaften.width, boxHoehe / eigenschaften.height);
			const breite = eigenschaften.width * faktor;
			const hoehe = eigenschaften.height * faktor;
			doc.setFillColor(245, 246, 245);
			doc.rect(x, bildY, boxBreite, boxHoehe, 'F');
			doc.addImage(
				foto.datenUrl,
				fotoFormat(foto),
				x + (boxBreite - breite) / 2,
				bildY + (boxHoehe - hoehe) / 2,
				breite,
				hoehe,
				undefined,
				'MEDIUM'
			);
		} catch {
			doc.setDrawColor(210, 214, 211);
			doc.rect(x, bildY, boxBreite, boxHoehe);
		}
	};
	const fotogruppe = (fotoIds: string[], label: string) => {
		const fotos = fotoIds.map((id) => protokoll.fotos.find((foto) => foto.id === id)).filter((foto): foto is Foto => Boolean(foto));
		for (let index = 0; index < fotos.length; index += 2) {
			braucht(62);
			const boxBreite = (INHALTSBREITE - 5) / 2;
			const reihe = fotos.slice(index, index + 2);
			reihe.forEach((foto, spalte) => {
				const x = RAND + spalte * (boxBreite + 5);
				bild(foto, x, y, boxBreite, 47);
				doc.setFont(PDF_SCHRIFT, 'normal');
				doc.setFontSize(7);
				doc.setTextColor(92, 100, 96);
				doc.text(
					`${sichereZeichen(label)} ${index + spalte + 1} · ${new Date(foto.aufgenommenAm).toLocaleString('de-DE', {
						dateStyle: 'short',
						timeStyle: 'short'
					})}`,
					x,
					y + 52
				);
			});
			y += 59;
		}
	};

	doc.setFont(PDF_SCHRIFT, 'bold');
	doc.setFontSize(17);
	doc.setTextColor(15, 91, 67);
	doc.text(BRAND.name, RAND, y + 5);
	doc.setFontSize(8);
	doc.setTextColor(85, 96, 90);
	doc.text(BRAND.claim, SEITENBREITE - RAND, y + 5, { align: 'right' });
	y += 14;
	doc.setDrawColor(24, 32, 28);
	doc.setLineWidth(0.8);
	doc.line(RAND, y, SEITENBREITE - RAND, y);
	y += 12;

	doc.setFont(PDF_SCHRIFT, 'bold');
	doc.setFontSize(19);
	setzeTextfarbe();
	doc.text(sichereZeichen(texte.titel(protokoll.art)), RAND, y);
	y += 8;
	doc.setFontSize(13);
	doc.text(sichereZeichen(protokoll.objekt.strasse || 'Ohne Adresse'), RAND, y);
	y += 6;
	doc.setFont(PDF_SCHRIFT, 'normal');
	doc.setFontSize(9);
	doc.setTextColor(85, 92, 88);
	doc.text(sichereZeichen(`${protokoll.objekt.plz} ${protokoll.objekt.ort}`.trim()), RAND, y);
	y += 7;
	const gesamtMaengel = protokoll.raeume.reduce(
		(summe, raum) => summe + raum.maengel.filter((mangel) => mangel.zustand === 'mangel').length,
		0
	);
	doc.text(
		`${texte.erstelltAm} ${new Date(protokoll.erstelltAm).toLocaleDateString('de-DE')}${
			gesamtMaengel > 0 ? ` · ${texte.maengelAnzahl(gesamtMaengel)}` : ''
		}`,
		RAND,
		y
	);
	y += 11;

	abschnitt(texte.raeume);
	if (protokoll.raeume.length === 0) {
		doc.setFontSize(9);
		doc.text(sichereZeichen(texte.keineRaeumeErfasst), RAND, y);
		y += 8;
	}
	for (const raum of protokoll.raeume) {
		braucht(18);
		doc.setFont(PDF_SCHRIFT, 'bold');
		doc.setFontSize(12);
		setzeTextfarbe();
		doc.text(sichereZeichen(raum.name), RAND, y);
		y += 7;
		if (raum.fotoIds.length > 0) fotogruppe(raum.fotoIds, texte.foto);
		if (raum.maengel.length === 0) {
			doc.setFont(PDF_SCHRIFT, 'normal');
			doc.setFontSize(9);
			doc.setTextColor(85, 92, 88);
			doc.text(sichereZeichen(texte.keineAuffaelligkeiten), RAND, y);
			y += 8;
		}
		for (const mangel of raum.maengel) {
			const text = `${mangel.gegenstand} - ${mangel.beschreibung} [${texte.zustand[mangel.zustand]}]`;
			doc.setFont(PDF_SCHRIFT, 'normal');
			doc.setFontSize(9);
			const umbruch = zeilen(text);
			braucht(umbruch.length * 4.5 + (mangel.fotoIds.length > 0 ? 62 : 5));
			setzeTextfarbe(40, 47, 43);
			doc.text(umbruch, RAND, y);
			y += umbruch.length * 4.5 + 3;
			if (mangel.fotoIds.length > 0) fotogruppe(mangel.fotoIds, texte.foto);
		}
		y += 4;
	}

	if (protokoll.zaehler.length > 0) {
		abschnitt(texte.zaehlerstaende);
		for (const zaehler of protokoll.zaehler) {
			braucht(12);
			doc.setFont(PDF_SCHRIFT, 'bold');
			doc.setFontSize(9.5);
			setzeTextfarbe();
			doc.text(sichereZeichen(texte.zaehlerArt[zaehler.art]), RAND, y);
			doc.setFont(PDF_SCHRIFT, 'normal');
			doc.text(sichereZeichen(`${zaehler.nummer || texte.nichtErfasst} · ${zaehler.stand || texte.nichtErfasst}`), RAND + 48, y);
			y += 7;
			if (zaehler.fotoIds.length > 0) fotogruppe(zaehler.fotoIds, texte.foto);
		}
	}

	if (protokoll.schluessel.length > 0) {
		abschnitt(texte.schluessel);
		for (const schluessel of protokoll.schluessel) {
			braucht(7);
			doc.setFont(PDF_SCHRIFT, 'normal');
			doc.setFontSize(9.5);
			setzeTextfarbe();
			doc.text(sichereZeichen(schluessel.art), RAND, y);
			doc.text(String(schluessel.anzahl), SEITENBREITE - RAND, y, { align: 'right' });
			y += 7;
		}
	}

	if (protokoll.beteiligte.length > 0) {
		abschnitt(texte.unterschriften);
		for (let index = 0; index < protokoll.beteiligte.length; index += 2) {
			braucht(42);
			const reihe = protokoll.beteiligte.slice(index, index + 2);
			const breite = (INHALTSBREITE - 8) / 2;
			reihe.forEach((beteiligter, spalte) => {
				const x = RAND + spalte * (breite + 8);
				doc.setDrawColor(205, 210, 207);
				doc.rect(x, y, breite, 27);
				if (beteiligter.unterschrift) {
					try {
						doc.addImage(beteiligter.unterschrift, 'PNG', x + 3, y + 2, breite - 6, 22, undefined, 'MEDIUM');
					} catch {
						// Ein unlesbares altes Signaturformat lässt das restliche PDF nicht scheitern.
					}
				}
				doc.setFont(PDF_SCHRIFT, 'bold');
				doc.setFontSize(8.5);
				setzeTextfarbe();
				doc.text(sichereZeichen(beteiligter.name || texte.nichtErfasst), x, y + 33);
				doc.setFont(PDF_SCHRIFT, 'normal');
				doc.setTextColor(85, 92, 88);
				doc.text(sichereZeichen(texte.rolle[beteiligter.rolle]), x + breite, y + 33, { align: 'right' });
			});
			y += 41;
		}
	}

	fuss();
	const blob = doc.output('blob');
	return { blob, dateiname: dateinameFuer(protokoll) };
}

export function ladePdfHerunter(blob: Blob, dateiname: string): void {
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = dateiname;
	link.click();
	window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
